/**
 * FinancialCategoryRules.js
 * Category Rules tab — add, edit, delete rules; re-categorize pending staging rows.
 *
 * API:
 *   GET    /fin-rules                — list rules
 *   POST   /fin-rules                — add rule
 *   PUT    /fin-rules/{id}           — update rule
 *   DELETE /fin-rules/{id}           — delete rule
 *   POST   /fin-rules/recategorize   — re-run auto-categorize on staging
 *   GET    /fin-pnl/categories       — category list
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "./apiClient";
import { useTheme, getT, glassCard } from "./ThemeContext";
import { PrimaryButton, GhostButton, DangerButton } from "./components/ThemedButton";
import FinancialSelect from "./FinancialSelect";
import { ALL_CATEGORIES } from "./constants/financialCategories";
import { useDateTimeFormat } from "./utils/formatters";

function Spinner({ T, size = 28 }) {
  return (
    <span style={{
      display:"inline-block", width:size, height:size, borderRadius:"50%",
      border:"2px solid "+T.divider, borderTopColor:T.yellow,
      animation:"rules-spin 0.7s linear infinite",
    }}>
      <style>{`@keyframes rules-spin{to{transform:rotate(360deg)}}`}</style>
    </span>
  );
}

export default function FinancialCategoryRules() {
  const { isDark } = useTheme();
  const T  = getT(isDark);
  const gc = glassCard(T);
  const { fmtDate } = useDateTimeFormat();

  const [rules,      setRules]      = useState([]);
  const [categories, setCategories] = useState(ALL_CATEGORIES);
  const [loading,    setLoading]    = useState(false);
  const [msg,        setMsg]        = useState(null);   // {type:"success"|"error", text}

  // ── new-rule form state ───────────────────────────────────────────────────
  const [newPattern,  setNewPattern]  = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [adding,      setAdding]      = useState(false);

  // ── inline edit state ─────────────────────────────────────────────────────
  const [editId,      setEditId]      = useState(null);
  const [editPattern, setEditPattern] = useState("");
  const [editCat,     setEditCat]     = useState("");
  const [saving,      setSaving]      = useState(false);

  // ── recategorize state ────────────────────────────────────────────────────
  const [recatBusy,   setRecatBusy]   = useState(false);

  // ── search filter ─────────────────────────────────────────────────────────
  const [search,  setSearch]  = useState("");
  const [sortKey, setSortKey] = useState("category");
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (col) => {
    setSortDir(prev => (sortKey === col ? (prev === "asc" ? "desc" : "asc") : "asc"));
    setSortKey(col);
  };

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/fin-rules"),
      api.get("/fin-pnl/categories"),
    ])
      .then(([r1, r2]) => {
        setRules(r1.data);
        setCategories([...new Set([...ALL_CATEGORIES, ...r2.data])].sort());
      })
      .catch(e => setMsg({ type:"error", text: e.response?.data?.detail || e.message }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  // ── add rule ──────────────────────────────────────────────────────────────
  const addRule = async () => {
    if (!newPattern.trim() || !newCategory.trim()) return;
    setAdding(true);
    try {
      const res = await api.post("/fin-rules", { pattern: newPattern.trim(), category: newCategory.trim() });
      setRules(prev => [...prev, res.data].sort((a,b) => a.category.localeCompare(b.category) || a.pattern.localeCompare(b.pattern)));
      setNewPattern(""); setNewCategory("");
      flash("success", "Rule added.");
    } catch (e) {
      flash("error", e.response?.data?.detail || e.message);
    } finally {
      setAdding(false);
    }
  };

  // ── start / cancel edit ───────────────────────────────────────────────────
  const startEdit = (rule) => {
    setEditId(rule.id);
    setEditPattern(rule.pattern);
    setEditCat(rule.category);
  };
  const cancelEdit = () => { setEditId(null); };

  // ── save edit ─────────────────────────────────────────────────────────────
  const saveEdit = async (id) => {
    if (!editPattern.trim() || !editCat.trim()) return;
    setSaving(true);
    try {
      const res = await api.put("/fin-rules/" + id, { pattern: editPattern.trim(), category: editCat.trim() });
      setRules(prev => prev.map(r => r.id === id ? res.data : r));
      setEditId(null);
      flash("success", "Rule updated.");
    } catch (e) {
      flash("error", e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── delete rule ───────────────────────────────────────────────────────────
  const deleteRule = async (id, pattern) => {
    if (!window.confirm('Delete rule: "' + pattern + '"?')) return;
    try {
      await api.delete("/fin-rules/" + id);
      setRules(prev => prev.filter(r => r.id !== id));
      flash("success", "Rule deleted.");
    } catch (e) {
      flash("error", e.response?.data?.detail || e.message);
    }
  };

  // ── re-categorize staging ─────────────────────────────────────────────────
  const recategorize = async () => {
    setRecatBusy(true);
    try {
      const res = await api.post("/fin-rules/recategorize");
      flash("success",
        res.data.approved + " staging row(s) auto-approved. " +
        res.data.remaining + " still pending.");
    } catch (e) {
      flash("error", e.response?.data?.detail || e.message);
    } finally {
      setRecatBusy(false);
    }
  };

  const filtered = rules
    .filter(r =>
      !search ||
      r.pattern.toLowerCase().includes(search.toLowerCase()) ||
      r.category.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      let av, bv;
      if      (sortKey === "pattern")  { av = a.pattern.toLowerCase();  bv = b.pattern.toLowerCase(); }
      else if (sortKey === "category") { av = a.category.toLowerCase(); bv = b.category.toLowerCase(); }
      else if (sortKey === "added")    { av = a.created_at;             bv = b.created_at; }
      else return 0;
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const thStyle = {
    padding:"10px 14px", fontWeight:800, fontSize:9.5,
    textTransform:"uppercase", letterSpacing:"0.10em",
    color:T.textMuted, borderBottom:"1px solid "+T.divider,
    whiteSpace:"nowrap",
  };

  const inputStyle = {
    background:T.inputBg, border:"1px solid "+T.inputBdr,
    borderRadius:8, color:T.inputText, fontSize:12,
    padding:"7px 10px", outline:"none", fontFamily:T.font, width:"100%",
    colorScheme: T.isDark ? "dark" : "light",
  };

  const selectStyle = {
    ...inputStyle, cursor:"pointer",
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

      {/* ── Toast ── */}
      {msg && (
        <div style={{
          padding:"11px 16px", borderRadius:10, fontSize:13, fontWeight:600,
          background: msg.type==="success"
            ? (T.isDark?"rgba(34,197,94,0.15)":"rgba(22,163,74,0.1)")
            : (T.isDark?"rgba(239,68,68,0.15)":"rgba(239,68,68,0.1)"),
          border:"1px solid "+(msg.type==="success"
            ? (T.isDark?"#22c55e55":"#16a34a44")
            : (T.isDark?"#ef444455":"#ef444433")),
          color: msg.type==="success" ? T.green : (T.red),
        }}>{msg.text}</div>
      )}

      {/* ── Header row: count + Re-categorize button ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div>
          <span style={{ fontSize:20, fontWeight:800, color:T.textPrimary }}>
            Category Rules
          </span>
          <span style={{ fontSize:13, color:T.textMuted, marginLeft:10 }}>
            {rules.length} rule{rules.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          {/* search */}
          <input
            placeholder="Search pattern or category…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle, width:220, padding:"6px 10px" }}
          />
          {/* Re-categorize button */}
          <PrimaryButton
            onClick={recategorize}
            disabled={recatBusy}
            style={{
              background: T.isDark?"rgba(139,92,246,0.18)":"rgba(124,58,237,0.1)",
              border:"1px solid "+(T.isDark?"#8b5cf655":"#7c3aed44"),
              color: T.isDark?"#c4b5fd":"#7c3aed",
              cursor: recatBusy ? "wait" : "pointer",
            }}
          >
            {recatBusy ? "Running…" : "⟳ Re-categorize Staging"}
          </PrimaryButton>
        </div>
      </div>

      {/* ── Add new rule form ── */}
      <div style={{ ...gc, borderRadius:14, padding:"16px 20px" }}>
        <div style={{ fontSize:12, fontWeight:700, color:T.textMuted, marginBottom:10,
                      textTransform:"uppercase", letterSpacing:"0.08em" }}>
          Add New Rule
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:10, alignItems:"end" }}>
          <div>
            <div style={{ fontSize:11, color:T.textMuted, marginBottom:4 }}>
              Pattern <span style={{ fontWeight:400 }}>(SQL LIKE — use % as wildcard)</span>
            </div>
            <input
              placeholder="e.g.  %RENT%  or  %GUSTO%"
              value={newPattern}
              onChange={e => setNewPattern(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addRule()}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={{ fontSize:11, color:T.textMuted, marginBottom:4 }}>Category</div>
            <FinancialSelect
              value={newCategory}
              onChange={v => setNewCategory(v)}
              options={categories}
              placeholder="— select category —"
              T={T}
              style={{ minWidth: 200, width: "100%" }}
            />
          </div>
          <PrimaryButton
            onClick={addRule}
            disabled={adding || !newPattern.trim() || !newCategory.trim()}
            style={{ padding:"8px 20px" }}
          >
            {adding ? "Adding…" : "+ Add Rule"}
          </PrimaryButton>
        </div>

        {/* Pattern hint */}
        <div style={{ fontSize:11, color:T.textMuted, marginTop:10, lineHeight:1.6 }}>
          <strong style={{ color:T.textSub }}>Pattern examples:</strong>
          {" "}
          <code style={{ background:T.glass, padding:"1px 6px", borderRadius:4 }}>%GUSTO%</code> matches any description containing "GUSTO" &nbsp;·&nbsp;
          <code style={{ background:T.glass, padding:"1px 6px", borderRadius:4 }}>%RENT%</code> matches "RENT" anywhere &nbsp;·&nbsp;
          <code style={{ background:T.glass, padding:"1px 6px", borderRadius:4 }}>ZELLE*</code> matches descriptions starting with "ZELLE"
        </div>
      </div>

      {/* ── Rules table ── */}
      {loading ? (
        <div style={{ display:"flex", justifyContent:"center", padding:48 }}>
          <Spinner T={T} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ ...gc, borderRadius:14, padding:48, textAlign:"center", color:T.textMuted }}>
          {search ? "No rules match your search." : "No rules yet. Add one above."}
        </div>
      ) : (
        <div style={{ ...gc, borderRadius:14, overflowX:"auto" }}>
          <table style={{ minWidth:"100%", fontSize:12, borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:"1px solid "+T.divider }}>
                <th style={{ ...thStyle, textAlign:"center", width:50 }}>#</th>
                {[
                  { label:"Pattern",  col:"pattern",  width:"auto" },
                  { label:"Category", col:"category", width:"auto" },
                  { label:"Added",    col:"added",    width:130 },
                ].map(({ label, col, width }) => (
                  <th key={col}
                    onClick={() => handleSort(col)}
                    style={{
                      ...thStyle, textAlign:"left", width,
                      color: sortKey===col ? T.yellow : undefined,
                      cursor:"pointer", userSelect:"none",
                    }}
                  >
                    {label}
                    <span style={{ marginLeft:4, opacity:sortKey===col?1:0.3, fontSize:10 }}>
                      {sortKey===col ? (sortDir==="asc" ? "▲" : "▼") : "⇅"}
                    </span>
                  </th>
                ))}
                <th style={{ ...thStyle, textAlign:"center", width:130 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((rule, idx) => {
                const isEditing = editId === rule.id;
                return (
                  <tr key={rule.id} style={{
                    borderBottom:"1px solid "+T.divider,
                    background: isEditing
                      ? (T.isDark?"rgba(247,206,11,0.05)":"rgba(247,206,11,0.03)")
                      : "",
                  }}>
                    {/* # */}
                    <td style={{ padding:"9px 14px", textAlign:"center", color:T.textMuted, fontSize:11 }}>
                      {idx + 1}
                    </td>

                    {/* Pattern */}
                    <td style={{ padding:"9px 14px" }}>
                      {isEditing ? (
                        <input
                          value={editPattern}
                          onChange={e => setEditPattern(e.target.value)}
                          style={{ ...inputStyle, fontSize:12 }}
                        />
                      ) : (
                        <code style={{
                          background:T.glass, padding:"2px 8px", borderRadius:5,
                          fontSize:12, color:T.textPrimary, fontFamily:"monospace",
                        }}>{rule.pattern}</code>
                      )}
                    </td>

                    {/* Category */}
                    <td style={{ padding:"9px 14px" }}>
                      {isEditing ? (
                        <FinancialSelect
                          value={editCat}
                          onChange={v => setEditCat(v)}
                          options={[
                            ...(!categories.includes(editCat) && editCat ? [editCat] : []),
                            ...categories,
                          ]}
                          T={T}
                          style={{ minWidth: 200 }}
                        />
                      ) : (
                        <span style={{
                          display:"inline-block",
                          background: T.isDark?"rgba(247,206,11,0.1)":"rgba(247,206,11,0.12)",
                          border:"1px solid "+T.yellow+"33",
                          color:T.accentText, borderRadius:6,
                          padding:"2px 9px", fontSize:11, fontWeight:600,
                        }}>{rule.category}</span>
                      )}
                    </td>

                    {/* Added */}
                    <td style={{ padding:"9px 14px", color:T.textMuted, fontSize:11, whiteSpace:"nowrap" }}>
                      {fmtDate(rule.created_at)}
                    </td>

                    {/* Actions */}
                    <td style={{ padding:"9px 14px", textAlign:"center" }}>
                      {isEditing ? (
                        <div style={{ display:"flex", gap:6, justifyContent:"center" }}>
                          <PrimaryButton
                            size="sm"
                            onClick={() => saveEdit(rule.id)}
                            disabled={saving}
                            style={{ padding:"4px 12px", fontSize:11 }}
                          >
                            {saving ? "…" : "Save"}
                          </PrimaryButton>
                          <GhostButton size="sm" onClick={cancelEdit}>Cancel</GhostButton>
                        </div>
                      ) : (
                        <div style={{ display:"flex", gap:6, justifyContent:"center" }}>
                          <GhostButton size="sm" onClick={() => startEdit(rule)}>Edit</GhostButton>
                          <DangerButton size="sm" onClick={() => deleteRule(rule.id, rule.pattern)}>Delete</DangerButton>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── How it works info box ── */}
      <div style={{
        ...gc, borderRadius:12, padding:"14px 18px",
        borderLeft:"3px solid "+T.yellow,
      }}>
        <div style={{ fontSize:12, fontWeight:700, color:T.accentText, marginBottom:6 }}>How rules work</div>
        <ul style={{ margin:0, paddingLeft:18, fontSize:12, color:T.textMuted, lineHeight:1.8 }}>
          <li>Rules match against the transaction <strong style={{color:T.textSub}}>Description</strong> using SQL LIKE patterns (<code style={{background:T.glass,padding:"0 4px",borderRadius:3}}>%</code> = any text).</li>
          <li>First matching rule wins — order rules from most specific to most general.</li>
          <li>After adding rules, click <strong style={{color:T.isDark?"#c4b5fd":"#7c3aed"}}>⟳ Re-categorize Staging</strong> to instantly approve any pending staging rows that now match.</li>
          <li>New uploads automatically apply all rules before any rows reach the staging area.</li>
        </ul>
      </div>

    </div>
  );
}
